import React, { useState, useEffect } from 'react'
import GoogleMapReact from 'google-map-react'

// tee/basket are stored as integer microdegrees, the geometry extras
// (routes, mandatories, boundaries, drop zones) as decimal degrees.
const toLatLng = (location) =>
  location
    ? {
        lat: location.lat * 0.000001,
        lng: location.lng * 0.000001,
      }
    : location

const coordToLatLng = (coord) =>
  coord ? { lat: coord.lat, lng: coord.lng } : null

// Colors mirror the Android app's ColorPalette.Map.
const COLORS = {
  routePalette: ['#4CAF50', '#29B6F6', '#FFCA28', '#AB47BC', '#FF7043'],
  mandatory: '#FFB300',
  ob: '#E53935',
  hazard: '#9C27B0',
  island: '#66BB6A',
  dropZone: '#1565C0',
}

const EARTH_RADIUS = 6378137 // meters
const ARROW_HEAD_LENGTH = 3.0 // meters
const ARROW_HEAD_HALF_WIDTH = 1.5 // meters

const toRad = (deg) => (deg * Math.PI) / 180
const toDeg = (rad) => (rad * 180) / Math.PI

// Spherical helpers matching Android's SphericalUtil so the mando arrowhead
// keeps its size/orientation regardless of latitude.
const computeHeading = (from, to) => {
  const fromLat = toRad(from.lat)
  const toLat = toRad(to.lat)
  const dLng = toRad(to.lng - from.lng)
  const y = Math.sin(dLng) * Math.cos(toLat)
  const x =
    Math.cos(fromLat) * Math.sin(toLat) -
    Math.sin(fromLat) * Math.cos(toLat) * Math.cos(dLng)
  return toDeg(Math.atan2(y, x))
}

const computeOffset = ({ lat, lng }, distance, heading) => {
  const d = distance / EARTH_RADIUS
  const h = toRad(heading)
  const lat1 = toRad(lat)
  const lng1 = toRad(lng)
  const sinLat = Math.sin(lat1) * Math.cos(d) + Math.cos(lat1) * Math.sin(d) * Math.cos(h)
  const lat2 = Math.asin(sinLat)
  const lng2 =
    lng1 +
    Math.atan2(
      Math.sin(h) * Math.sin(d) * Math.cos(lat1),
      Math.cos(d) - Math.sin(lat1) * sinLat
    )
  return { lat: toDeg(lat2), lng: toDeg(lng2) }
}

// Triangle (tip at `to`) for an arrowhead pointing along the `from` -> `to` heading.
const arrowHeadTriangle = (from, to) => {
  const heading = computeHeading(from, to)
  const base = computeOffset(to, ARROW_HEAD_LENGTH, heading + 180)
  return [
    to,
    computeOffset(base, ARROW_HEAD_HALF_WIDTH, heading + 270),
    computeOffset(base, ARROW_HEAD_HALF_WIDTH, heading + 90),
  ]
}

function CourseMap({ course = null }) {
  const defaultZoom = 15
  const [center, setCenter] = useState(toLatLng(course.pos))
  const [holes, setHoles] = useState([])
  const [holesWithLocation, setHolesWithLocation] = useState([])

  const mapRef = React.createRef()

  useEffect(() => {
    setCenter(toLatLng(course.pos))
    const _holes = course.holes.map((hole) => {
      const { tee, basket } = hole
      return { ...hole, tee: toLatLng(tee), basket: toLatLng(basket) }
    })
    const _holesWithLocation = _holes.filter(
      ({ tee, basket }) => !!tee || !!basket
    )
    setHoles(_holes)
    setHolesWithLocation(_holesWithLocation)
  }, [course])

  const renderNoLocations = () => {
    return (
      <div className="alert alert-danger">
        This course layout is missing hole location information
      </div>
    )
  }

  const zoomToHoleBounds = (map) => {
    const bounds = new window.google.maps.LatLngBounds()
    const extend = (point) => {
      if (point) bounds.extend(new window.google.maps.LatLng(point.lat, point.lng))
    }
    holesWithLocation.forEach((hole) => {
      extend(hole.tee)
      extend(hole.basket)
      hole.routes?.forEach((route) => route.points?.forEach((p) => extend(p)))
      hole.mandatories?.forEach((m) => m.points?.forEach((p) => extend(p)))
      hole.boundaries?.forEach((b) => b.points?.forEach((p) => extend(p)))
      hole.dz?.forEach((dz) => extend(dz.location))
    })
    map.fitBounds(bounds)
  }

  const createTee = (map, tee, index) => {
    new window.google.maps.Marker({
      position: tee,
      label: {
        text: `${index + 1}`,
        color: '#fff',
      },
      map,
    })
  }

  const createBasket = (map, basket) => {
    // Circle 1
    new window.google.maps.Circle({
      strokeColor: '#08c',
      strokeOpacity: 0.8,
      strokeWeight: 1,
      fillColor: '#08c',
      fillOpacity: 0.35,
      map,
      center: basket,
      radius: 10,
    })

    // Circle 2
    new window.google.maps.Circle({
      strokeColor: '#888',
      strokeOpacity: 0.8,
      strokeWeight: 1,
      fillColor: '#888',
      fillOpacity: 0.35,
      map,
      center: basket,
      radius: 20,
    })
  }

  const createHoleLine = (map, tee, basket) => {
    new window.google.maps.Polyline({
      path: [{ ...tee }, { ...basket }],
      geodesic: true,
      strokeColor: '#fff',
      strokeOpacity: 1.0,
      strokeWeight: 2,
      map,
    })
  }

  // A route is a suggested path: tee -> landing zone points -> basket.
  const createRoute = (map, tee, basket, route, colorIndex) => {
    const points = (route.points || []).map(coordToLatLng).filter(Boolean)
    if (points.length === 0) return

    const color =
      COLORS.routePalette[colorIndex % COLORS.routePalette.length]
    const path = [tee, ...points, basket].filter(Boolean)
    new window.google.maps.Polyline({
      path,
      geodesic: true,
      strokeColor: color,
      strokeOpacity: 0.9,
      strokeWeight: 3,
      map,
      zIndex: 2,
    })
  }

  // A directional mando: a line (post -> direction) with an arrowhead at the
  // post pointing back toward it, showing the required passing direction.
  const createMandatory = (map, mandatory) => {
    const points = (mandatory.points || []).map(coordToLatLng).filter(Boolean)
    if (points.length >= 2) {
      const post = points[0]
      const direction = points[1]
      new window.google.maps.Polyline({
        path: [post, direction],
        geodesic: true,
        strokeColor: COLORS.mandatory,
        strokeOpacity: 1.0,
        strokeWeight: 3,
        map,
        zIndex: 4,
      })
      new window.google.maps.Polygon({
        paths: arrowHeadTriangle(direction, post),
        strokeColor: COLORS.mandatory,
        strokeOpacity: 1.0,
        strokeWeight: 1,
        fillColor: COLORS.mandatory,
        fillOpacity: 1.0,
        map,
        zIndex: 4,
      })
    } else if (points.length === 1) {
      // Legacy single-point mando: a simple marker circle.
      new window.google.maps.Circle({
        center: points[0],
        radius: 3,
        strokeColor: COLORS.mandatory,
        strokeOpacity: 1.0,
        strokeWeight: 2,
        fillColor: COLORS.mandatory,
        fillOpacity: 1.0,
        map,
        zIndex: 4,
      })
    }
  }

  // OB, hazard or island boundary. An open OB is a line; hazard, island and a
  // closed OB are filled areas (island interior is the safe zone).
  const createBoundary = (map, feature) => {
    const points = (feature.points || []).map(coordToLatLng).filter(Boolean)
    if (points.length === 0) return

    const baseColor =
      feature.type === 'HAZARD'
        ? COLORS.hazard
        : feature.type === 'ISLAND'
        ? COLORS.island
        : COLORS.ob

    if (feature.type === 'OB' && !feature.closed) {
      new window.google.maps.Polyline({
        path: points,
        geodesic: true,
        strokeColor: baseColor,
        strokeOpacity: 1.0,
        strokeWeight: 3,
        map,
        zIndex: 1,
      })
    } else {
      const fillOpacity = feature.type === 'ISLAND' ? 0.15 : 0.27
      new window.google.maps.Polygon({
        paths: points,
        strokeColor: baseColor,
        strokeOpacity: 1.0,
        strokeWeight: 2,
        fillColor: baseColor,
        fillOpacity,
        map,
        zIndex: 1,
      })
    }
  }

  // An alternative tee used after a penalty, with a reference line to the basket.
  const createDropZone = (map, dz, basket) => {
    const location = coordToLatLng(dz.location)
    if (!location) return

    if (basket) {
      new window.google.maps.Polyline({
        path: [location, basket],
        geodesic: true,
        strokeColor: COLORS.dropZone,
        strokeOpacity: 0.6,
        strokeWeight: 2,
        map,
        zIndex: 3,
      })
    }
    new window.google.maps.Circle({
      center: location,
      radius: 2,
      strokeColor: COLORS.dropZone,
      strokeOpacity: 1.0,
      strokeWeight: 2,
      fillColor: COLORS.dropZone,
      fillOpacity: 0.85,
      map,
      zIndex: 3,
    })
  }

  const onGoogleApiLoaded = (map) => {
    mapRef.current = map

    zoomToHoleBounds(map)

    holes.forEach((hole) => {
      const { tee, basket } = hole

      // Boundaries first so hole lines and geometry draw on top.
      hole.boundaries?.forEach((feature) => createBoundary(map, feature))

      // Routes replace the straight hole line: only draw it when there are none.
      const hasRoutes = hole.routes && hole.routes.length > 0
      tee && basket && !hasRoutes && createHoleLine(map, tee, basket)

      hole.routes?.forEach((route, index) =>
        createRoute(map, tee, basket, route, index)
      )
      hole.mandatories?.forEach((mandatory) => createMandatory(map, mandatory))
      hole.dz?.forEach((dz) => createDropZone(map, dz, basket))
    })

    // Draw tees and baskets last so their labels/circles stay readable.
    holes.forEach(({ tee, basket }, index) => {
      tee && createTee(map, tee, index)
      basket && createBasket(map, basket)
    })
  }

  const renderMap = () => {
    return (
      // Important! Always set the container height explicitly
      <div style={{ height: '50vh', width: '100%' }}>
        <GoogleMapReact
          bootstrapURLKeys={{ key: process.env.REACT_APP_MAPS_KEY }}
          defaultCenter={center}
          defaultZoom={defaultZoom}
          yesIWantToUseGoogleMapApiInternals={true}
          onGoogleApiLoaded={({ map }) => onGoogleApiLoaded(map)}
          options={{
            mapTypeControl: true,
            mapTypeId: window.google.maps.MapTypeId.SATELLITE,
          }}
        ></GoogleMapReact>
      </div>
    )
  }

  if (holesWithLocation.length === 0) {
    return renderNoLocations()
  }

  return renderMap()
}

export default CourseMap
